/**
 * Maps feature (M3 plan R14, order `FEATURE_ORDER.maps`, reach mode only): the galaxy and system maps over the
 * running game (spec §6.1, plan §2.9 flow 3).
 *
 * - It reads its own G / View-hold edges from `ctx.input` every frame (`readMapInput`, with its own previous state),
 *   so nothing held across an open or a close fires twice. It does not open while paused or while another modal
 *   (a station panel, a dialog) is up.
 * - Opening: `ctx.ui.modal++` with an Esc handler (via `enterModal`, which also releases pointer lock), then on the
 *   first open a lazy `import('../../maps/view')` (the view is its own chunk), then the system map of the current
 *   system (or, from the Wire, the galaxy map focused on a system: `open(ctx, systemId)`).
 * - Closing: G (or View held), Esc, pad B at the top level, or the view's close button. It undoes the above and
 *   calls `ctx.ui.relock()` from the closing input.
 * - `frame` passes the map actions to the view while open; `overlay` draws the map over the finished frame.
 *
 * - M4 (plan I7): with the `strategic` service up, the first open also loads and installs the economy overlays
 *   (`src/ui/strategic/overlays.ts`, a lazy chunk); a failure there only logs, and the map opens without them.
 *   M8 TR-2: it passes `overlayIntel(ctx)`, so the price layers show the pilot's price log, not perfect information.
 *
 * `frame` and `overlay` allocate nothing apart from the open and close events (D-014).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { enterModal } from '../../app/ui'
import { logger } from '../../core/logger'
import { defaultFactionsConfig } from '../../galaxy/layout/config'
import { dangerOf } from '../../galaxy/layout/danger'
import { loadReachMapsConfig, type ReachMapsConfig } from '../../maps/model/config'
import { loadMapsViewConfig, type MapsViewConfig } from '../../maps/view/config'
import { createMapActions, createMapInputPrev, readMapInput, type MapActions, type MapInputPrev } from '../../maps/view/input'
import type { MapView, MapViewModule } from '../../maps/view/types'
import { loadReachControls, type ReachControls } from '../../reach/controls'
import type { OverlayIntel } from '../../ui/strategic/overlays'
import { playUiSound } from '../audio/feature'
import { mapHulks } from '../hulks/feature'

export type MapsPhase = 'closed' | 'opening' | 'open'

export interface MapsFeature extends GameFeature {
  /** Where the map is: closed, loading its view, or open. */
  readonly phase: MapsPhase
  /** The view once loaded (kept across closes). */
  readonly view: MapView | null
  /**
   * Opens the map (the debug API, tests and the Wire; players use G or View held). No-op unless closed in reach
   * mode. With `focusSystemId` (the Wire, M4 I6/I7) the galaxy map opens on that system (`MapView.focusSystem`);
   * while the map is opening or open, the focus still applies.
   */
  open(ctx: GameContext, focusSystemId?: string): void
  /** Closes the map; `relock` (default true) asks for pointer lock again when no other modal is open. */
  close(relock?: boolean): void
  /**
   * Dashboard W4b C3: the return-to-dashboard hook. The next close (of an opening or open map) calls `fn` instead of
   * re-locking the pointer, so a map launched from Nav › Map returns there. One-shot; null clears it.
   */
  returnTo(fn: (() => void) | null): void
  frame(ctx: GameContext, f: FrameInfo): void
  overlay(ctx: GameContext, f: FrameInfo): void
}

export interface MapsFeatureOptions {
  /** Loads the view chunk (default: `import('../../maps/view')`). */
  loadView?: () => Promise<MapViewModule>
  /** A UI click (default: the audio feature's `uiClick`). */
  playUi?: () => void
  /** The page origin for share links (default: `location.origin`). */
  origin?: () => string
  /** M4 (plan I7): installs the economy overlays when the strategic service is up (default: the lazy import). */
  loadOverlays?: (ctx: GameContext) => Promise<void>
}

/**
 * M8 TR-2: what the economy overlays read of the pilot. The career (its price log) comes from the `m2` service on
 * each rebuild (null without a career, so the layers read live); the galaxy and the fog of war from the Reach.
 */
export function overlayIntel(ctx: Pick<GameContext, 'reach' | 'services'>): OverlayIntel {
  return {
    career: () => ctx.services.maybe('m2')?.career() ?? null,
    reach: () => ctx.reach,
  }
}

function defaultLoadOverlays(ctx: GameContext): Promise<void> {
  const strategic = ctx.services.maybe('strategic')
  if (strategic === undefined) return Promise.resolve()
  return import('../../ui/strategic/overlays').then((m) => {
    m.installEconomyOverlays(strategic, { intel: overlayIntel(ctx) })
  })
}

export function createMapsFeature(opts: MapsFeatureOptions = {}): MapsFeature {
  const loadView = opts.loadView ?? ((): Promise<MapViewModule> => import('../../maps/view'))
  const playUi = opts.playUi ?? ((): void => playUiSound('uiClick'))
  const origin = opts.origin ?? ((): string => (typeof location === 'undefined' ? '' : location.origin))
  const loadOverlays = opts.loadOverlays ?? defaultLoadOverlays
  let overlaysPromise: Promise<void> | null = null

  let active = false
  let phase: MapsPhase = 'closed'
  let view: MapView | null = null
  /** A system the next open (or the open map) focuses on (the Wire). */
  let pendingFocus: string | null = null

  function applyFocus(v: MapView): void {
    if (pendingFocus === null) return
    const id = pendingFocus
    pendingFocus = null
    v.focusSystem(id)
  }
  /** The one-shot return hook (`returnTo`): called on the next close instead of a relock. */
  let onReturn: (() => void) | null = null
  let modulePromise: Promise<MapViewModule> | null = null
  let leave: ((relock?: boolean) => void) | null = null
  let openToken = 0
  let ctxRef: GameContext | null = null
  let controls: ReachControls | null = null
  let viewCfg: MapsViewConfig | null = null
  let mapsCfg: ReachMapsConfig | null = null
  const prev: MapInputPrev = createMapInputPrev()
  const actions: MapActions = createMapActions()

  /** Esc from the loop (or P / pad Start): the view may consume it for a prompt; otherwise the map closes. */
  const onEscape = (): boolean => {
    if (phase === 'open' && view !== null && view.escape()) return true
    if (ctxRef !== null) feature.close()
    return true
  }

  function createView(ctx: GameContext, mod: MapViewModule): MapView {
    const reach = ctx.reach
    if (reach === null || viewCfg === null || mapsCfg === null) throw new Error('Maps: no reach runtime')
    return mod.createMapView({
      core: reach,
      sim: ctx.sim,
      overlayRoot: ctx.overlayRoot,
      ui: ctx.ui,
      maps: mapsCfg,
      view: viewCfg,
      factions: defaultFactionsConfig(),
      danger: (i: number): number => {
        const s = reach.galaxy.systems[i]
        return s === undefined ? 0 : dangerOf(s)
      },
      origin: origin(),
      // M9 CH-12e: the found, unfinished hulks on the system map (the career read on each rebuild).
      hulks: (layout) => mapHulks(reach, layout, ctx.services.maybe('m2')?.career() ?? null),
      playUi,
      requestClose: () => feature.close(),
    })
  }

  const feature: MapsFeature = {
    name: 'maps',
    order: FEATURE_ORDER.maps,
    get phase(): MapsPhase {
      return phase
    },
    get view(): MapView | null {
      return view
    },

    init(ctx: GameContext): void {
      if (ctx.reach === null) return
      controls = loadReachControls()
      viewCfg = loadMapsViewConfig()
      mapsCfg = loadReachMapsConfig()
      ctxRef = ctx
      active = true
    },

    open(ctx: GameContext, focusSystemId?: string): void {
      if (!active) return
      if (focusSystemId !== undefined) pendingFocus = focusSystemId
      if (phase === 'open' && view !== null) {
        applyFocus(view)
        return
      }
      if (phase !== 'closed') return
      phase = 'opening'
      leave = enterModal(ctx.ui, onEscape)
      playUi()
      const token = ++openToken
      modulePromise ??= loadView()
      overlaysPromise ??= Promise.resolve()
        .then(() => loadOverlays(ctx))
        .catch((err: unknown) => {
          logger.warn('Map: the economy overlays failed to load', err)
        })
      Promise.all([modulePromise, overlaysPromise])
        .then(([mod]) => mod)
        .then((mod) => {
          if (phase !== 'opening' || token !== openToken) return
          view ??= createView(ctx, mod)
          view.show()
          phase = 'open'
          applyFocus(view)
        })
        .catch((err: unknown) => {
          logger.error('The map failed to load', err)
          modulePromise = null
          if (token === openToken) feature.close(false)
        })
    },

    close(relock = true): void {
      pendingFocus = null
      if (phase === 'closed') return
      view?.hide()
      phase = 'closed'
      openToken++
      const l = leave
      leave = null
      const back = onReturn
      onReturn = null
      l?.(relock && back === null)
      if (relock) playUi()
      back?.()
    },

    returnTo(fn: (() => void) | null): void {
      onReturn = fn
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      if (!active || controls === null || viewCfg === null) return
      readMapInput(ctx.input.keys, ctx.input.pad, controls, viewCfg, f.dt, prev, actions)
      if (f.paused) return
      if (phase === 'closed') {
        if (actions.toggle && ctx.ui.modal === 0) feature.open(ctx)
        return
      }
      if (phase === 'opening') {
        if (actions.toggle || actions.back) feature.close()
        return
      }
      const v = view
      if (v === null) return
      if (actions.toggle && !v.capturingText && !v.busy) {
        feature.close()
        return
      }
      if (v.update(actions, f.dt) === 'close') feature.close()
    },

    overlay(ctx: GameContext): void {
      if (phase === 'open' && view !== null) view.render(ctx.renderer)
    },

    dispose(): void {
      if (ctxRef !== null) feature.close(false)
      view?.dispose()
      view = null
      active = false
      ctxRef = null
    },
  }
  return feature
}

export default createMapsFeature()
